import React, { useState, useMemo, useEffect } from 'react';
import { celebrationList } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { ordinal, formatDate } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import { PageHeader, Button, Badge, Input, SearchBar, Pagination, Notification } from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Monday..Sunday of the week `offset` weeks from the CURRENT week — so offset 0
// is this week (the week containing today), -1 last week, +1 next week.
const getWeekRange = (offset) => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun .. 6=Sat
  const daysSinceMonday = (dayOfWeek + 6) % 7; // Mon->0, Sun->6

  const start = new Date(today);
  start.setDate(today.getDate() - daysSinceMonday + offset * 7);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const firstNameChar = (name) => (String(name || '?').trim().charAt(0) || '?').toUpperCase();

// These were hardcoded to en-US, which puts the month before the day
// ("September 23, 2026") while the rest of the app follows the parish's date
// format — day first by default. The range label and the day headings
// therefore disagreed with every other screen (CHM-135).
const formatDateLabel = (date) => (date ? formatDate(isoOf(date), '') : '');

const formatDayOnly = (date) =>
  (date
    ? `${date.toLocaleDateString('en-GB', { weekday: 'long' })}, ${pad(date.getDate())} ${date.toLocaleDateString('en-GB', { month: 'short' })}`
    : '');

const formatWordDate = (date) => {
  if (!date) return '';
  const d = pad(date.getDate());
  const m = date.toLocaleDateString('en-GB', { month: 'short' });
  return `${d} ${m} ${date.getFullYear()} - ${date.toLocaleDateString('en-GB', { weekday: 'long' })}`;
};

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Initial-only avatar — this is an announcement list, so it fetches no photos. */
const Avatar = ({ name, tone, isDarkMode }) => {
  const anniv = tone === 'anniversary';
  return (
    <div style={{
      width: '50px', height: '50px', borderRadius: '10px', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: anniv ? (isDarkMode ? '#1B2C3E' : '#E8F0FE') : (isDarkMode ? '#3A2030' : '#F3E5F5'),
      color: anniv ? (isDarkMode ? '#A8CBF0' : '#1A4A7A') : (isDarkMode ? '#F0B6CE' : '#6B4277'),
      fontSize: '20px', fontWeight: 'bold'
    }}>
      {firstNameChar(name)}
    </div>
  );
};

const BirthdayAnniversary = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // Printing is the one thing on this screen that can fail, so it needs
  // somewhere to say so.
  const { notification, notify, dismiss } = useNotification();

  const [weekOffset, setWeekOffset] = useState(0);
  const [mode, setMode] = useState('week');
  // The From/To fields open showing this week's range, matching the default view.
  const thisWeek = getWeekRange(0);
  const [customStart, setCustomStart] = useState(() => isoOf(thisWeek.start));
  const [customEnd, setCustomEnd] = useState(() => isoOf(thisWeek.end));
  const [appliedRange, setAppliedRange] = useState<{ start: Date; end: Date } | null>(null);

  const [nameQuery, setNameQuery] = useState('');
  const debouncedSearch = useDebouncedValue(nameQuery.trim(), 300);

  const PAGE_SIZE = getPreferences().pageSize;
  const [bPageNum, setBPageNum] = useState(1);
  const [aPageNum, setAPageNum] = useState(1);

  const { start, end } = useMemo(
    () => (mode === 'custom' && appliedRange ? appliedRange : getWeekRange(weekOffset)),
    [mode, appliedRange, weekOffset]
  );
  const fromStr = isoOf(start);
  const toStr = isoOf(end);
  const fromMd = fromStr.slice(5);

  // The occurrence lands in the start year, unless the range wraps New Year and
  // this date is on the January side.
  const enrich = useMemo(() => (r: any) => {
    const [mm, dd] = String(r.md || '').split('-');
    const eventYear = String(r.md) >= fromMd ? start.getFullYear() : end.getFullYear();
    const eventDate = new Date(eventYear, +mm - 1, +dd);
    const srcYear = parseInt(String(r.sourceDate || '').slice(0, 4), 10);
    const years = Number.isFinite(srcYear) ? eventYear - srcYear : null;
    return { ...r, eventDate, years };
  }, [fromMd, start, end]);

  // A new range or search returns to page 1.
  useEffect(() => { setBPageNum(1); setAPageNum(1); }, [fromStr, toStr, debouncedSearch]);

  const { data: bRows, raw: bRaw, loading: bLoading } = useFetchList<any>(
    (p, o) => celebrationList.page(p, o),
    { params: { type: 'BIRTHDAY', from: fromStr, to: toStr, q: debouncedSearch, page: bPageNum - 1, size: PAGE_SIZE } }
  );
  const { data: aRows, raw: aRaw, loading: aLoading } = useFetchList<any>(
    (p, o) => celebrationList.page(p, o),
    { params: { type: 'ANNIVERSARY', from: fromStr, to: toStr, q: debouncedSearch, page: aPageNum - 1, size: PAGE_SIZE } }
  );

  const birthdays = useMemo(() => bRows.map(enrich), [bRows, enrich]);
  const anniversaries = useMemo(() => aRows.map(enrich), [aRows, enrich]);

  /**
   * Both lists as one announcement sheet, in the order they fall. The screen
   * shows them as cards in two columns; on paper a single dated list is what
   * gets read out.
   */
  const sheetRows = useMemo(() => [
    ...birthdays.map((m) => ({ ...m, occasion: 'Birthday' })),
    ...anniversaries.map((m) => ({ ...m, occasion: 'Anniversary' }))
  ]
    .sort((a, b) => String(a.md || '').localeCompare(String(b.md || '')))
    .map((m) => ({
      name: m.name || '',
      occasion: m.occasion,
      date: formatDayOnly(m.eventDate),
      years: m.years == null ? '' : m.years,
      memberId: m.memberId || '',
      familyId: m.familyId || ''
    })), [birthdays, anniversaries]);
  const bTotal = getTotal(bRaw);
  const aTotal = getTotal(aRaw);

  const pager = (num, total, setNum) => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return {
      currentPage: num, totalPages, totalItems: total, itemsPerPage: PAGE_SIZE, setCurrentPage: setNum,
      next: () => setNum((p) => Math.min(p + 1, totalPages)),
      prev: () => setNum((p) => Math.max(p - 1, 1)),
      isFirst: num <= 1, isLast: num >= totalPages
    };
  };
  const bPage = pager(bPageNum, bTotal, setBPageNum);
  const aPage = pager(aPageNum, aTotal, setAPageNum);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    const from = new Date(customStart);
    const to = new Date(customEnd);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return;
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    setAppliedRange({ start: from, end: to });
    setMode('custom');
  };

  const backToWeekly = () => { setMode('week'); setAppliedRange(null); setWeekOffset(0); };



  const columnStyles = {
    birthday: { backgroundColor: isDarkMode ? '#2A1620' : '#F9F0F4', color: isDarkMode ? '#F0B6CE' : '#8A1C40', borderTop: '4px solid #C9658E' },
    anniversary: { backgroundColor: isDarkMode ? '#141F2B' : '#F0F4F9', color: isDarkMode ? '#A8CBF0' : '#1A4A7A', borderTop: '4px solid #5A92C8' }
  };
  const cardStyle = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '15px', backgroundColor: t.bgMain, border: `1px solid ${t.border}`,
    borderRadius: '10px', boxShadow: t.isDarkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.03)'
  };

  const renderPerson = (person, tone) => {
    const badge = tone === 'anniversary'
      ? (person.years != null ? `${ordinal(person.years)} Anniv.` : 'Anniversary')
      : (person.years != null ? `Turning ${person.years}` : 'Birthday');
    return (
      <div key={`${tone}-${person.id}`} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Avatar name={person.name} tone={tone} isDarkMode={isDarkMode} />
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', color: t.textMain, fontWeight: 'bold' }}>
              {person.name}
              {tone === 'anniversary' && person.couple && (
                <span style={{ fontSize: '12px', color: t.textFaint, fontWeight: 'normal' }}> (couple)</span>
              )}
            </h4>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: t.textFaint }}>
              {person.familyId} • {person.memberId}
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '15px', color: t.textMain, fontWeight: 'bold', marginBottom: '6px' }}>
            {formatDayOnly(person.eventDate)}
          </div>
          <Badge tone={tone === 'anniversary' ? 'accent' : 'error'}>{badge}</Badge>
        </div>
      </div>
    );
  };

  const emptyState = (message, loading) => (
    <div style={{ padding: '40px 20px', textAlign: 'center', backgroundColor: t.bgSubtle, border: `1px dashed ${t.borderStrong}`, borderRadius: '8px', color: t.textFaint, fontSize: '15px' }}>
      {loading ? 'Loading…' : message}
    </div>
  );


  const rangeLabel = mode === 'custom'
    ? 'Custom Report: '
    : weekOffset === 0 ? 'This Week: ' : weekOffset < 0 ? 'Past Week: ' : 'Future Week: ';

  const columnHeading = (tone, icon, label, count) => (
    <div style={{ ...columnStyles[tone], padding: '15px 20px', borderRadius: '8px', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
      <span className="notranslate" translate="no" style={{ marginRight: '10px', fontSize: '22px' }}>{icon}</span>
      {label} <span style={{ fontSize: '15px', opacity: 0.75, marginLeft: '8px' }}>({count})</span>
    </div>
  );


  return (
    <div style={styles.container}>
      <div>
        <Notification notification={notification} onClose={dismiss} />

        <PageHeader
          icon="🎉"
          title="Announcements"
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Button variant="tab" onClick={() => { setMode('week'); setAppliedRange(null); setWeekOffset((w) => w - 1); }}>← Previous</Button>
              {(weekOffset !== 0 || mode === 'custom') && (<Button variant="secondary" onClick={backToWeekly}>Current</Button>)}
              <Button variant="tab" onClick={() => { setMode('week'); setAppliedRange(null); setWeekOffset((w) => w + 1); }}>Next →</Button>
              <PrintButton
                module="Birthday & Anniversary"
                documentType="CELEBRATION_SHEET"
                size="sm"
                title="Celebration-Sheet"
                data={() => ({
                  title: 'Birthdays & Anniversaries',
                  reference: `${fromStr} to ${toStr}`,
                  rowCount: sheetRows.length,
                  rows: sheetRows
                })}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            </div>
          }
        />

        <SearchBar
          value={nameQuery}
          onChange={setNameQuery}
          onClear={() => setNameQuery('')}
          placeholder="Search by name, Member ID or Family ID…"
        />

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap', marginBottom: '15px', padding: '12px 15px', backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`, borderRadius: t.radius.lg }}>
          <Input label="From" type="date" containerStyle={{ marginBottom: 0, minWidth: '160px' }} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
          <Input label="To" type="date" containerStyle={{ marginBottom: 0, minWidth: '160px' }} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          <Button onClick={applyCustomRange} disabled={!customStart || !customEnd}>Apply Range</Button>
          {mode === 'custom' && (<Button variant="secondary" onClick={backToWeekly}>Back to Weekly</Button>)}
        </div>

        <p style={{ margin: '-5px 0 20px 0', color: t.textAccent, fontSize: '15px' }}>
          {rangeLabel}<strong>{formatDateLabel(start)}</strong> — <strong>{formatDateLabel(end)}</strong>
        </p>
      </div>

      <div className="screen-layout" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {columnHeading('birthday', '🎂', 'Birthdays', bTotal)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {birthdays.length === 0 ? emptyState('No birthdays found for this range.', bLoading) : birthdays.map((m) => renderPerson(m, 'birthday'))}
          </div>
          {bTotal > PAGE_SIZE && <Pagination {...bPage} itemsOnPage={birthdays.length} label="birthdays" isDarkMode={isDarkMode} />}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {columnHeading('anniversary', '💍', 'Anniversaries', aTotal)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {anniversaries.length === 0 ? emptyState('No anniversaries found for this range.', aLoading) : anniversaries.map((m) => renderPerson(m, 'anniversary'))}
          </div>
          {aTotal > PAGE_SIZE && <Pagination {...aPage} itemsOnPage={anniversaries.length} label="anniversaries" isDarkMode={isDarkMode} />}
        </div>
      </div>

    </div>
  );
};

export default BirthdayAnniversary;
